import { DateTime } from "luxon";
import type { Banner, BannerStatus } from "@services/banners/commands/cmdListBanners";
import type { BannerPayload } from "@services/banners/commands/cmdCreateBanner";
import { etToIso, formatEt, isoToEt } from "@/shared/utils/date-utils/easternTime";
import { Paths } from "@/paths";

/*
    The Banner editor's logic, kept out of the page so it can be tested without rendering the
    date pickers. Start and End are ET DateTimes here and UTC ISO only in toPayload. `now` is
    passed in, so every function is pure.
*/

export const MAX_MESSAGE = 100;
export const MAX_LINK_TEXT = 50;

export interface BannerFormValues {
    message: string;
    linkText: string;
    linkUrl: string;
    start: DateTime | null;
    end: DateTime | null;
}

export type BannerFormField = keyof BannerFormValues;
export type BannerFormErrors = Partial<Record<BannerFormField, string>>;

/** Router state into the editor. MPN-2266's Partner Dashboard preview returns the draft here. */
export interface BannerEditorRouterState {
    bannerDraft: BannerPayload;
    bannerId?: string;
}

/** The editor's URL for a banner. Shared with the Partner Dashboard's banner controls (MPN-2266). */
export const bannerEditPath = (uuid: string) =>
    `${import.meta.env.BASE_URL}${Paths.BANNER_EDIT.replace(":bannerId", uuid)}`;

export function emptyForm(): BannerFormValues {
    return { message: "", linkText: "", linkUrl: "", start: null, end: null };
}

export function fromBanner(b: Banner): BannerFormValues {
    return fromPayload({ ...b, is_draft: b.status === "Draft" });
}

export function fromPayload(p: BannerPayload): BannerFormValues {
    return {
        message: p.message,
        linkText: p.link_text,
        linkUrl: p.link_url,
        start: isoToEt(p.starts_at),
        end: isoToEt(p.ends_at),
    };
}

export function toPayload(v: BannerFormValues, asDraft: boolean): BannerPayload {
    return {
        message: v.message.trim(),
        link_text: v.linkText.trim(),
        link_url: v.linkUrl.trim(),
        starts_at: etToIso(v.start),
        ends_at: etToIso(v.end),
        is_draft: asDraft,
    };
}

/**
 * An edit's PATCH. starts_at is left out when the start is unchanged, so the server keeps a
 * Live banner's real start instead of treating it as moved and clamping it to now. Both sides
 * go through etToIso, so a stored start with microseconds still compares equal.
 */
export function toPatch(v: BannerFormValues, asDraft: boolean, loadedStartsAt: string | null): Partial<BannerPayload> {
    const { starts_at, ...rest } = toPayload(v, asDraft);
    return starts_at === etToIso(isoToEt(loadedStartsAt)) ? rest : { ...rest, starts_at };
}

const validStart = (v: BannerFormValues) => (v.start?.isValid ? v.start : null);

/** The rules shown while typing, not only after a save attempt (the design): https and End after Start. */
export function validateLive(v: BannerFormValues): BannerFormErrors {
    const errors: BannerFormErrors = {};
    const linkUrl = v.linkUrl.trim();
    const start = validStart(v);
    if (linkUrl && !linkUrl.startsWith("https://")) errors.linkUrl = "Link URL must start with https://";
    if (start && v.end?.isValid && v.end <= start) errors.end = "End must be after Start.";
    return errors;
}

/**
 * Every rule, for a save attempt; the live rules win on the same field. Empty means valid. The
 * Message input is capped at MAX_MESSAGE, so there is no length rule for it.
 */
export function validate(v: BannerFormValues, asDraft: boolean): BannerFormErrors {
    const errors: BannerFormErrors = {};
    const message = v.message.trim();
    const linkText = v.linkText.trim();
    const linkUrl = v.linkUrl.trim();

    if (!message) errors.message = "Message is required.";

    if (linkText && !linkUrl) errors.linkUrl = "Add a link URL, or clear the link text.";
    if (linkUrl && !linkText) errors.linkText = "Add link text, or clear the link URL.";
    if (linkText.length > MAX_LINK_TEXT) errors.linkText = "Keep link text to 50 characters.";

    const start = validStart(v);
    if (!asDraft && !start) errors.start = "Start is required to schedule.";
    else if (v.start && !v.start.isValid) errors.start = "Pick a start date and time, or leave it blank.";
    if (v.end && !v.end.isValid) errors.end = "Pick an end date and time, or leave it blank.";

    return { ...errors, ...validateLive(v) };
}

/**
 * The other Live or Scheduled banner these dates overlap, first by start, or null. Mirrors the
 * server's find_overlap ([starts_at, ends_at) windows); the server's 409 stays the authority.
 * A past start needs no clamp to now: every Live or Scheduled banner ends after now.
 */
export function findOverlap(v: BannerFormValues, others: Banner[]): Banner | null {
    const start = validStart(v);
    if (!start) return null;
    const end = v.end?.isValid ? v.end : null;
    const overlapping = others.filter((o) => {
        const oStart = isoToEt(o.starts_at);
        const oEnd = isoToEt(o.ends_at);
        if (!oStart || (o.status !== "Live" && o.status !== "Scheduled")) return false;
        return (!oEnd || oEnd > start) && (!end || oStart < end);
    });
    return overlapping.sort((a, b) => (a.starts_at ?? "").localeCompare(b.starts_at ?? ""))[0] ?? null;
}

export function counterText(v: BannerFormValues): string {
    return `${v.message.length}/${MAX_MESSAGE} characters`;
}

/** The publish button. A Live banner keeps its start (toPatch leaves it out); a past start is clamped to now. */
export function publishLabel(v: BannerFormValues, now: DateTime, loadedStatus?: BannerStatus): string {
    if (loadedStatus === "Live") return "Save Changes";
    const start = validStart(v);
    return start && start <= now ? "Post Now" : "Schedule";
}

/** The summary bar's text after "Audience: All Partner Dashboard users | ". */
export function windowSummary(v: BannerFormValues, now: DateTime): string {
    const start = validStart(v);
    const goesLive = !start ? "once a start is set" : start <= now ? "now" : formatEt(start);
    const comesDown = v.end?.isValid ? formatEt(v.end) : "when turned off";
    return `Goes live ${goesLive} and comes down ${comesDown}.`;
}
